/**
 * Guard for markdown html_inline / html_block on web.
 *
 * Assistant replies can include raw HTML/SVG. Injecting unbounded markup
 * (especially SVG with huge width/height) is a known tab-killer: one dumped
 * diagram can add hundreds of MB of compositor memory on Linux software
 * rasterizers. Oversized or clearly-dangerous payloads render as plain text.
 */

export const MAX_RAW_MESSAGE_HTML_CHARS = 8000;
export const MAX_RAW_HTML_DIMENSION_PX = 5000;

// `style` is included because a model-authored stylesheet is not scoped to the
// message and can restyle the whole app.
const DISALLOWED_TAG = /<\s*\/?\s*(?:script|style|iframe|object|embed|link|meta|base|form)\b/i;

// Inline event handlers in attribute position: after whitespace, a slash, or
// a quote. An unanchored /on\w+\s*=/ also fires on prose such as
// "monster = big"; requiring a delimiter keeps that, while still catching
// `<img/onerror=` and `<img src="x"onerror=`.
const INLINE_EVENT_HANDLER = /(?:^|[\s/"'])on[a-z]+\s*=/i;

const DANGEROUS_URL = /(?:javascript|data\s*:\s*text\/html|vbscript)\s*:/i;

const DIMENSION_PROPERTY = /^(?:(?:min-|max-)?(?:width|height|inline-size|block-size))$/i;
const LENGTH = /^\+?((?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)(px|in|cm|mm|q|pt|pc|%)?$/i;
const PX_PER_UNIT = { px: 1, in: 96, cm: 96 / 2.54, mm: 96 / 25.4, q: 96 / 101.6, pt: 96 / 72, pc: 16 };

function isBoundedDimension(value) {
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === 'auto') return true;
  const match = LENGTH.exec(normalized);
  // Context-dependent lengths (em/vh/calc/var/etc.) cannot establish a pixel
  // bound here. Render those payloads as text rather than guessing a scale.
  if (!match) return false;
  const size = Number(match[1]);
  const unit = match[2] || 'px';
  if (!Number.isFinite(size)) return false;
  if (unit === '%') return size <= 100;
  return size * PX_PER_UNIT[unit] <= MAX_RAW_HTML_DIMENSION_PX;
}

function hasUnsafeStyle(style) {
  // CSS escapes can disguise property names; keep this guard consistent even
  // in server rendering environments without a CSS parser.
  if (style.includes('\\')) return true;
  return style.replace(/\/\*[\s\S]*?\*\//g, '').split(';').some(declaration => {
    const separator = declaration.indexOf(':');
    if (separator < 0) return false;
    const property = declaration.slice(0, separator).replace(/\/\*[\s\S]*?\*\//g, '').trim();
    const value = declaration.slice(separator + 1).replace(/\/\*[\s\S]*?\*\//g, '').replace(/\s*!important\s*$/i, '').trim();
    return DIMENSION_PROPERTY.test(property) && !isBoundedDimension(value);
  });
}

function hasUnsafeSizingAttribute(name, value) {
  if (/^(?:width|height)$/i.test(name)) return !isBoundedDimension(value);
  return name.toLowerCase() === 'style' && hasUnsafeStyle(value);
}

function hasOversizedDimension(content) {
  if (typeof document !== 'undefined') {
    // Detached parsing decodes HTML entities and follows browser attribute
    // syntax without laying out or painting an oversized SVG.
    const template = document.createElement('template');
    template.innerHTML = content;
    for (const element of template.content.querySelectorAll('*')) {
      for (const attribute of element.attributes) {
        if (hasUnsafeSizingAttribute(attribute.name, attribute.value)) return true;
      }
    }
    return false;
  }
  // Conservative SSR fallback; ambiguous/entity-encoded sizing becomes text.
  const attributes = /\b(width|height|style)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;
  let match;
  while ((match = attributes.exec(content)) !== null) {
    const value = match[2] ?? match[3] ?? match[4];
    if (value.includes('&') || hasUnsafeSizingAttribute(match[1], value)) return true;
  }
  return false;
}

export function shouldRenderRawMessageHtml(content) {
  if (typeof content !== 'string' || !content) return false;
  if (content.length > MAX_RAW_MESSAGE_HTML_CHARS) return false;
  if (DISALLOWED_TAG.test(content)) return false;
  if (INLINE_EVENT_HANDLER.test(content)) return false;
  if (DANGEROUS_URL.test(content)) return false;
  if (hasOversizedDimension(content)) return false;
  return true;
}
